import type { HistoryPoint } from '@netdeck/contracts';

/** Ширина и высота системы координат мини-графика. */
export const RTT_CHART_WIDTH = 100;
export const RTT_CHART_HEIGHT = 24;

/** Зазор между столбиками, но не больше четверти шага: иначе они исчезнут. */
const BAR_GAP = 0.6;

/**
 * Наименьшая заметная высота столбика.
 *
 * Отклик в десятые доли миллисекунды рядом с пиком в сотни даёт столбик
 * в ноль пикселей, и полоса проверок выглядит дырявой там, где всё
 * отвечало. Пенёк честнее пустоты: проверка была.
 */
const BAR_MIN_HEIGHT = 1;

export interface RttBar {
  x: number;
  width: number;
  y: number;
  height: number;
  /** В этом отрезке были неудачные проверки — столбик красный. */
  failed: boolean;
}

export interface RttGeometry {
  /** Ломаная времени отклика; пустая строка — рисовать нечего. */
  line: string;
  /** Те же данные столбиками — второй вид того же графика. */
  bars: RttBar[];
  /** Наибольший отклик за период — подпись шкалы. */
  peakMs: number | null;
  /** Доли ширины, где проверки не прошли. */
  failures: { x: number; width: number }[];
}

/**
 * Геометрия мини-графика RTT (§11, фаза 4, задача 5).
 *
 * Считается отдельно от разметки: это единственное место, где легко
 * ошибиться на единицу, и проверять его глазами на живых данных — дорого.
 *
 * Провалы рисуются полосами, а не разрывом линии: график в двадцать четыре
 * пикселя высотой, и пропущенная точка на нём не читается, а красная полоса
 * видна сразу — именно она и есть то, ради чего на график смотрят.
 *
 * Оба вида графика считаются здесь разом и по одной шкале: пик подписан
 * один, и переключение вида не должно менять высоту столбика относительно
 * линии — иначе два вида рассказывали бы про историю разное.
 */
export function rttGeometry(points: HistoryPoint[]): RttGeometry {
  if (points.length === 0) {
    return { line: '', bars: [], peakMs: null, failures: [] };
  }

  const peak = points.reduce<number>((max, point) => Math.max(max, point.maxRttMs ?? 0), 0);
  const step = RTT_CHART_WIDTH / points.length;

  const gap = Math.min(BAR_GAP, step / 4);

  const failures: { x: number; width: number }[] = [];
  const bars: RttBar[] = [];
  const segments: string[] = [];
  let drawing = false;

  points.forEach((point, index) => {
    const x = index * step + step / 2;
    const failed = point.totalCount > point.okCount;

    if (failed) {
      failures.push({ x: index * step, width: step });
    }

    const barHeight = barHeightOf(point.avgRttMs, peak, failed);

    if (barHeight !== null) {
      bars.push({
        x: round(index * step + gap / 2),
        width: round(step - gap),
        y: round(RTT_CHART_HEIGHT - barHeight),
        height: round(barHeight),
        failed,
      });
    }

    if (point.avgRttMs === null) {
      // Разрыв: линия не должна перепрыгивать пропуск прямой.
      drawing = false;
      return;
    }

    // Пик нулевой, когда отклик везде нулевой (петля на Windows) —
    // делить на него нельзя, линия кладётся по низу.
    const y =
      peak === 0 ? RTT_CHART_HEIGHT : RTT_CHART_HEIGHT - (point.avgRttMs / peak) * RTT_CHART_HEIGHT;

    segments.push(`${drawing ? 'L' : 'M'} ${round(x)} ${round(y)}`);
    drawing = true;
  });

  return {
    line: segments.join(' '),
    bars,
    peakMs: peak > 0 ? peak : null,
    failures,
  };
}

/**
 * Высота столбика. `null` — столбика нет вовсе.
 *
 * Отрезок без единого ответа рисуется столбиком во всю высоту, и только
 * если проверки в нём падали: провал должен быть виден, а вот отрезок,
 * где проверок просто не было — выключенный монитор, перерыв в работе
 * платформы, — обязан остаться пустым местом, а не выдавать себя за сбой.
 */
function barHeightOf(avgRttMs: number | null, peak: number, failed: boolean): number | null {
  if (avgRttMs === null) {
    return failed ? RTT_CHART_HEIGHT : null;
  }

  // Пик нулевой, когда отклик везде нулевой (петля на Windows): шкалы
  // из этого не выйдет, и все столбики становятся пеньками.
  if (peak === 0) {
    return BAR_MIN_HEIGHT;
  }

  return Math.max(BAR_MIN_HEIGHT, (avgRttMs / peak) * RTT_CHART_HEIGHT);
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}
